import type { StatusKind } from '@munifocus/ui';
import type { ContractorInvoiceDisplayStatus, TimeEntryStatus } from '@munifocus/contracts-gov';
import { frenchFirstOfMonth } from '@munifocus/utils';

/**
 * Small presentational helpers for the operations screens. Pure, so the
 * arithmetic a supervisor approves against is pinned by a test.
 */

/** "1 h 05", "45 min", "0 min" — the same in English and French. */
export function formatMinutes(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
}

/**
 * Hours and minutes typed in two boxes, to whole minutes, or null when either
 * is not a whole number in range or the total is outside 1–1440.
 */
export function toMinutes(hours: string, minutes: string): number | null {
  const h = hours.trim() === '' ? 0 : Number(hours);
  const m = minutes.trim() === '' ? 0 : Number(minutes);
  if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || m < 0 || m > 59) return null;
  const total = h * 60 + m;
  return total >= 1 && total <= 1440 ? total : null;
}

/** Split whole minutes back into the two boxes. */
export function splitForInput(total: number): { hours: string; minutes: string } {
  return { hours: String(Math.floor(total / 60)), minutes: String(total % 60) };
}

/** Kilometres with one decimal under 10 km, whole above, in the reader's locale. */
export function formatDistance(meters: number, locale: string): string {
  const km = meters / 1000;
  const fmt = new Intl.NumberFormat(locale.startsWith('fr') ? 'fr-CA' : 'en-CA', {
    maximumFractionDigits: km < 10 ? 1 : 0,
  });
  return `${fmt.format(km)} km`;
}

/** The current month and the eleven before it, newest first, as `YYYY-MM`. */
export function recentMonths(now: Date = new Date(), count = 12): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

/** "October 2026" / "octobre 2026". */
export function monthLabel(month: string, locale: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(locale.startsWith('fr') ? 'fr-CA' : 'en-CA', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(y, m - 1, 1))),
  );
}

export function invoiceStatusKind(status: ContractorInvoiceDisplayStatus): StatusKind {
  switch (status) {
    case 'draft':
      return 'neutral';
    case 'sent':
      return 'pending';
    case 'paid':
      return 'success';
    case 'overdue':
      return 'warning';
    case 'void':
      return 'rejected';
  }
}

export function timeStatusKind(status: TimeEntryStatus): StatusKind {
  return status === 'approved' ? 'approved' : status === 'rejected' ? 'rejected' : 'pending';
}

/** Cents as an amount for an input box: "85.00" or "85,00". */
export function centsForInput(cents: number | null, locale: string): string {
  if (cents === null) return '';
  const text = (cents / 100).toFixed(2);
  return locale.startsWith('fr') ? text.replace('.', ',') : text;
}
